import IconBranch from "central-icons/IconBranch";
import { useAtomValue, useSetAtom } from "jotai";
import { CirclePlus, MenuIcon, XIcon } from "lucide-react";
import { useCallback, type FC } from "react";
import { useMatch } from "react-router-dom";

import { OnaLettermark } from "@/assets/icons/OnaLettermark";
import { OnaWordmark } from "@/assets/icons/OnaWordmark";
import type { BaseIconProps, Size } from "@/assets/icons/utils";
import { fromGitpodSize, withGitpodSize } from "@/assets/icons/utils";
import { detailsPanelToggleAtom, superOnaContentTabAtom } from "@/atoms/ui-state";
import { EnvironmentHeaderDropdown } from "@/components/environments/SuperOnaConversationHeader";
import { Button } from "@/components/flexkit/Button";
import { Tabs, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { SkeletonText } from "@/components/podkit/loading/Skeleton";
import { useEnvironmentConversation, getAgentExecution } from "@/hooks/use-environment-conversation";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { useEnvironment } from "@/queries/environment-queries";
import { agentExecutionName } from "@/utils/agent-executions";
import { getEnvironmentDisplayName } from "@/utils/environment-display-name";

type SuperOnaMobileHeaderProps = {
    mobileMenuOpen: boolean;
    onToggleMobileMenu: () => void;
    disableMobileMenu: boolean;
    isNewConversationPage: boolean;
    onNewEnvironment: () => void;
};

const IconX: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <XIcon size={sizeMap[size]} />;
};

const IconMenu: FC<{ size: Size }> = ({ size }) => {
    const sizeMap: Record<Size, number> = { sm: 16, base: 20, lg: 24 };
    return <MenuIcon size={sizeMap[size]} />;
};

const OnaLettermarkIcon: FC<BaseIconProps> = ({ size, className, ...props }) => {
    const px = fromGitpodSize(size);
    return <OnaLettermark width={px} height={px} className={className} {...props} />;
};

const IconBranchSized = withGitpodSize(IconBranch);

/** Resolves environment + agent execution data for the details route. */
const DetailsPageIdentity: FC = () => {
    const match = useMatch("/details/:environmentId/*");
    const environmentId = match?.params.environmentId ?? "";
    const { data: environment } = useEnvironment(environmentId);
    const { environmentConversation } = useEnvironmentConversation(environmentId);
    const agentExecution = getAgentExecution(environmentConversation);

    const { value: isOnaAIEnabled } = useFlagOnaAIEnabled();
    const conversationName = agentExecution ? agentExecutionName(agentExecution) : "Conversation";
    const displayName = getEnvironmentDisplayName(environment, conversationName, !!isOnaAIEnabled);

    const setSuperOnaContentTab = useSetAtom(superOnaContentTabAtom);
    const handleOpenEnvironmentDetails = useCallback(() => {
        setSuperOnaContentTab((prev) => ({ tab: "environment", seq: prev.seq + 1 }));
    }, [setSuperOnaContentTab]);

    return (
        <SkeletonText ready={!!environment} className="w-40" size="lg">
            {environment && (
                <EnvironmentHeaderDropdown
                    environment={environment}
                    agentExecution={agentExecution ?? undefined}
                    displayName={displayName}
                    onOpenEnvironmentDetails={handleOpenEnvironmentDetails}
                />
            )}
        </SkeletonText>
    );
};

/** Conversation / Output toggle shown on the details page. */
const DetailsPanelToggle: FC = () => {
    const detailsPanelToggle = useAtomValue(detailsPanelToggleAtom);
    if (!detailsPanelToggle) return null;

    const { isOpen: isOutputOpen, toggle } = detailsPanelToggle;
    const activeTab = isOutputOpen ? "output" : "conversation";

    return (
        <Tabs value={activeTab} className="shrink-0 [container-type:normal]">
            <TabsList className="min-h-0 flex-row gap-1 p-0.5">
                <TabsTrigger
                    value="conversation"
                    aria-label="Conversation"
                    LeadingIcon={OnaLettermarkIcon}
                    className="h-7 w-8 min-w-8 flex-grow-0 justify-center gap-0 px-1.5 py-0"
                    onClick={() => isOutputOpen && toggle()}
                    data-tracking-id="conversation"
                />
                <TabsTrigger
                    value="output"
                    aria-label="Changes"
                    LeadingIcon={IconBranchSized}
                    className="h-7 w-8 min-w-8 flex-grow-0 justify-center gap-0 px-1.5 py-0"
                    onClick={() => !isOutputOpen && toggle()}
                    data-tracking-id="changes"
                />
            </TabsList>
        </Tabs>
    );
};

export const SuperOnaMobileHeader: FC<SuperOnaMobileHeaderProps> = ({
    mobileMenuOpen,
    onToggleMobileMenu,
    disableMobileMenu,
    isNewConversationPage,
    onNewEnvironment,
}) => {
    const isDetailsPage = !!useMatch("/details/:environmentId/*");

    return (
        <header className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
                {!disableMobileMenu && (
                    <Button
                        variant="link"
                        size="md"
                        onClick={onToggleMobileMenu}
                        className="shrink-0 text-content-primary"
                        LeadingIcon={mobileMenuOpen ? IconX : IconMenu}
                        aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                        data-tracking-id="toggle-mobile-menu"
                    />
                )}
                {isDetailsPage ? <DetailsPageIdentity /> : <OnaWordmark className="h-4 w-14" />}
            </div>
            {isDetailsPage && <DetailsPanelToggle />}
            {isNewConversationPage && (
                <Button
                    variant="link"
                    size="md"
                    className="text-content-primary"
                    onClick={onNewEnvironment}
                    aria-label="New environment"
                    data-tracking-id="new-environment"
                >
                    <CirclePlus size={20} />
                </Button>
            )}
        </header>
    );
};
